// src/utils/useActor.js
import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../auth/AuthProvider';
import { useOrgId } from './tenant';

/**
 * Returns the current logged-in admin's profile info as used for audit trails.
 * Source of truth:
 *  - orgs/{orgId}/members/{uid}
 * Fallbacks:
 *  - auth.displayName, then email, then uid
 */
export default function useActor() {
  const { user } = useAuth();
  const orgId = useOrgId();

  const [profile, setProfile] = useState(null);
  const uid = user?.uid || null;
  const email = user?.email || '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orgId || !uid) {
        setProfile(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId, 'members', uid));
        if (cancelled) return;
        setProfile(snap.exists() ? (snap.data() || {}) : null);
      } catch {
        if (!cancelled) setProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, uid]);

  const name =
    String(profile?.displayName || '').trim() ||
    String(user?.displayName || '').trim() ||
    (email ? email.split('@')[0] : '') ||
    (uid ? `user:${uid.slice(0, 6)}` : 'unknown');

  return useMemo(
    () => ({
      orgId,
      uid: uid || '',
      email: email || '',
      name,
      profile,
    }),
    [orgId, uid, email, name, profile]
  );
}
